"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, CircleCheck as CheckCircle2, ArrowRight, Calendar, ShieldCheck, Search, TrendingUp, UserCheck } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Link } from "@/components/common/Link";
const trustPillars = [{
  title: "Direct Access to Me",
  desc: "You work exclusively with Yassine Essaadi. No account managers, no junior staff."
}, {
  title: "Plymouth & UK Reach",
  desc: "Targeted strategies tailored to capture high-intent local searchers and convert leads."
}, {
  title: "Transparent & Honest",
  desc: "No vague retainers or hidden jargon—just clear deliverables and realistic growth."
}];
const deliverables = ["Google Business Profile Optimisation", "Localised Keyword & Content Strategy", "Geotargeted Technical Schema Markup", "Local Citation Audits & Authority Building"];
export default function Localseohero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="localseohero" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 lg:py-28 text-foreground">
      <div className="pointer-events-none absolute -top-24 left-1/2 -z-10 h-96 w-96 -translate-x-1/2 rounded-full bg-primary/5 blur-3xl sm:w-[600px]" />
      <div className="pointer-events-none absolute right-0 top-1/3 -z-10 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          <motion.div className="lg:col-span-7" initial="hidden" animate="visible" variants={containerVariants}>
            <motion.div variants={itemVariants} className="mb-4">
              <Badge variant="outline" className="inline-flex items-center gap-2 border-primary/20 bg-secondary/60 px-3 py-1.5 text-xs font-medium text-foreground sm:text-sm rounded-full">
                <MapPin className="size-4 text-accent shrink-0" />
                <span>Local SEO Consultant in Plymouth &amp; South West</span>
              </Badge>
            </motion.div>

            <motion.h1 variants={itemVariants} className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl lg:leading-tight">
              Local SEO Consultant in Plymouth
            </motion.h1>

            <motion.p variants={itemVariants} className="mt-5 text-base text-muted-foreground sm:text-lg md:text-xl leading-relaxed">
              I help Plymouth businesses and regional brands dominate local search results, capture high-converting customers on Google Maps, and build sustainable online growth. When you work with me, you get direct expert guidance—never an agency middleman.
            </motion.p>

            <motion.div variants={itemVariants} className="mt-6 space-y-3">
              {trustPillars.map((pillar, i) => <div key={i} data-index={i} className="flex items-start gap-3 text-sm sm:text-base text-foreground">
                  <div className="mt-1 flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <CheckCircle2 className="size-4" />
                  </div>
                  <div>
                    <span className="font-semibold">{pillar.title}:</span>{" "}
                    <span className="text-muted-foreground">{pillar.desc}</span>
                  </div>
                </div>)}
            </motion.div>

            <motion.div variants={itemVariants} className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md transition-all rounded-md px-6 py-3 h-auto font-medium" asChild>
                <Link to="/ContactPage">
                  <SafeHydrate><Calendar className="size-4 mr-2" /></SafeHydrate>
                  Book a Free SEO Consultation
                </Link>
              </Button>

              <Button size="lg" variant="outline" className="border-border bg-card text-card-foreground hover:bg-accent hover:text-accent-foreground shadow-xs transition-all rounded-md px-6 py-3 h-auto font-medium" asChild>
                <Link to="/CaseStudiesPage">
                  Explore My Case Studies
                  <ArrowRight className="size-4 ml-2" />
                </Link>
              </Button>
            </motion.div>

            <motion.div variants={itemVariants} className="mt-8 flex flex-wrap items-center gap-y-2 text-xs text-muted-foreground border-t border-border pt-4">
              <span className="inline-flex items-center gap-1.5 mr-4">
                <ShieldCheck className="size-4 text-primary" />
                No Agency Lock-In Contracts
              </span>
              <span className="inline-flex items-center gap-1.5 mr-4">
                <UserCheck className="size-4 text-primary" />
                100% Hands-On Delivery
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Search className="size-4 text-primary" />
                Ethical &amp; Sustainable Strategy
              </span>
            </motion.div>
          </motion.div>

          <motion.div className="lg:col-span-5" initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }}>
            <div className="relative rounded-2xl border border-border bg-card p-6 shadow-xl sm:p-8">
              <div className="flex items-center gap-4 pb-6 border-b border-border">
                <SafeHydrate><Avatar className="size-16 border-2 border-primary/20 shadow-sm">
                  <AvatarImage src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_plymouth_seo_consultant.png" alt="Yassine Essaadi - Local SEO Consultant in Plymouth" />
                  <AvatarFallback className="bg-primary text-primary-foreground font-semibold">
                    YE
                  </AvatarFallback>
                </Avatar></SafeHydrate>
                <div>
                  <h2 className="font-serif text-lg font-bold text-card-foreground">
                    Yassine Essaadi
                  </h2>
                  <p className="text-xs text-muted-foreground font-medium">
                    Independent Freelance SEO Consultant
                  </p>
                  <div className="mt-1 flex items-center gap-1.5 text-xs text-accent font-medium">
                    <MapPin className="size-3.5" />
                    <span>Based in the UK &bull; Serving Plymouth</span>
                  </div>
                </div>
              </div>

              <div className="mt-6 rounded-xl bg-muted/60 p-4 border border-border/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    My Local SEO Focus
                  </span>
                  <Badge variant="secondary" className="text-[11.5px] uppercase tracking-wider bg-accent/20 text-foreground font-semibold border-none">
                    Plymouth &amp; Regional
                  </Badge>
                </div>
                <p className="text-xs text-card-foreground leading-relaxed">
                  {"\"I build tailored search strategies designed to put your Plymouth business in front of local decision-makers right when they're ready to buy.\""}
                </p>
              </div>

              <div className="mt-6 space-y-2.5">
                <p className="text-xs font-semibold text-card-foreground uppercase tracking-wider mb-3">
                  What I execute for local visibility:
                </p>
                {deliverables.map((item, i) => <div key={i} data-index={i} className="flex items-center gap-2.5 text-xs sm:text-sm text-card-foreground">
                    <TrendingUp className="size-4 shrink-0 text-primary" />
                    <span>{item}</span>
                  </div>)}
              </div>

              <div className="mt-6 pt-5 border-t border-border">
                <Link to="/LocalSEOPage" className="group flex items-center justify-between text-xs font-semibold text-primary hover:text-primary/80 transition-colors">
                  <span>Learn more about my Local SEO Services</span>
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}